/**
 * The ghost-text mirror. All geometry is quarantined here on purpose: jsdom
 * has no layout (every rect is 0×0), so tests assert only on the DOM this
 * module builds — structure, text, visibility — never on positions.
 *
 * Technique: a body-appended overlay sized to the input's border box, holding
 * the full value in a transparent span plus the grey remainder after it.
 * Mirroring the whole line (same font, padding, alignment) is what makes any
 * text-align correct for free — the typed text lays out exactly where the
 * input laid it out, and the ghost lands right after it. The input's value is
 * never touched until the visitor explicitly accepts.
 */

export interface GhostOverlay {
  show(prefix: string, remainder: string): void;
  hide(): void;
  readonly visible: boolean;
}

/** Styles copied from the input on every show — cheap at typing frequency and
 * survives responsive breakpoints without a ResizeObserver. */
const MIRRORED = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontStretch",
  "letterSpacing",
  "textTransform",
  "lineHeight",
  "direction",
  "boxSizing",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
] as const;

/** Visible ghosts needing repositioning when the page moves under them. */
const repositioners = new Set<() => void>();
let listenersInstalled = false;

function installSharedListeners(): void {
  if (listenersInstalled) return;
  listenersInstalled = true;
  const repositionAll = () => {
    for (const reposition of repositioners) reposition();
  };
  window.addEventListener("resize", repositionAll);
  // Capture phase so scrolls inside nested containers (sticky navbars, modal
  // bodies) are seen too — scroll events don't bubble.
  window.addEventListener("scroll", repositionAll, { capture: true, passive: true });
}

export function createOverlay(
  input: HTMLInputElement,
  onAccept: () => void,
): GhostOverlay {
  let container: HTMLDivElement | null = null;
  let line: HTMLSpanElement | null = null;
  let prefixSpan: HTMLSpanElement | null = null;
  let ghostSpan: HTMLSpanElement | null = null;
  let visible = false;

  // Lazily built: pages where the visitor never gets a suggestion (or never
  // focuses the input) add nothing to the DOM.
  function ensureBuilt(): void {
    if (container) return;
    container = document.createElement("div");
    container.setAttribute("data-slater-typeahead-overlay", "");
    container.setAttribute("aria-hidden", "true");
    const cs = container.style;
    cs.position = "absolute";
    cs.margin = "0";
    cs.overflow = "hidden";
    cs.pointerEvents = "none";
    cs.zIndex = "2147483646";
    cs.background = "transparent";
    cs.borderStyle = "solid";
    cs.borderColor = "transparent";
    // Flex centers the line vertically the way an input centers its text,
    // without guessing at line-height arithmetic.
    cs.display = "flex";
    cs.alignItems = "center";

    line = document.createElement("span");
    line.style.whiteSpace = "pre";
    line.style.display = "inline-block";

    prefixSpan = document.createElement("span");
    prefixSpan.setAttribute("data-slater-typeahead-prefix", "");
    prefixSpan.style.color = "transparent";

    ghostSpan = document.createElement("span");
    ghostSpan.setAttribute("data-slater-typeahead-ghost", "");
    // The one interactive part: tapping the ghost accepts it (the mobile
    // accept gesture — there is no Tab key on a touch keyboard).
    ghostSpan.style.pointerEvents = "auto";
    ghostSpan.style.cursor = "text";
    ghostSpan.addEventListener("pointerdown", (event) => {
      // Keep focus (and the caret) in the input instead of moving it here.
      event.preventDefault();
      onAccept();
    });

    line.appendChild(prefixSpan);
    line.appendChild(ghostSpan);
    container.appendChild(line);
    document.body.appendChild(container);
  }

  function reposition(): void {
    if (!container || !line) return;
    const rect = input.getBoundingClientRect();
    const cs = container.style;
    cs.left = `${rect.left + window.scrollX}px`;
    cs.top = `${rect.top + window.scrollY}px`;
    cs.width = `${rect.width}px`;
    cs.height = `${rect.height}px`;
    // Long values scroll inside the input; mirror the offset so the ghost
    // stays glued to the text, and let overflow:hidden clip the rest.
    line.style.transform = `translateX(${-input.scrollLeft}px)`;
  }

  function show(prefix: string, remainder: string): void {
    ensureBuilt();
    if (!container || !line || !prefixSpan || !ghostSpan) return;

    prefixSpan.textContent = prefix;
    ghostSpan.textContent = remainder;

    const computed = window.getComputedStyle(input);
    const cs = container.style;
    for (const prop of MIRRORED) cs[prop] = computed[prop];
    cs.borderStyle = "solid";
    cs.borderColor = "transparent";
    // Follow the input's own text color so the ghost reads on dark navbars
    // too; the attribute is the CSS override hook (needs !important).
    ghostSpan.style.color = computed.color;
    ghostSpan.style.opacity = "0.45";
    // text-align, mapped to flex (flex-start/end already honor direction).
    const align = computed.textAlign;
    cs.justifyContent =
      align === "center" ? "center" : align === "right" || align === "end" ? "flex-end" : "flex-start";

    cs.display = "flex";
    if (!visible) {
      visible = true;
      repositioners.add(reposition);
      installSharedListeners();
    }
    reposition();
  }

  function hide(): void {
    if (!visible) return;
    visible = false;
    repositioners.delete(reposition);
    if (container) container.style.display = "none";
  }

  return {
    show,
    hide,
    get visible() {
      return visible;
    },
  };
}
